import React from "react";
import { Words, FadeUp, PopIn } from "@/components/Reveal";
import PillButton from "@/components/PillButton";

const TIERS = [
  {
    name: "At the Table",
    price: "Free",
    status: "Available Now",
    features: [
      "A seat at the community table.",
      "Occasional updates, no newsletter, nothing sold — the same rhythm Stephanie's list has always run on.",
      "Occasional community notes",
      "Early word when a new episode drops",
      "An invite to open community moments",
    ],
    cta: { label: "Join the Community", href: "/community" },
    highlight: true,
  },
  {
    name: "The Inner Circle",
    price: "$8/ month",
    status: "Coming Soon",
    features: [
      "Everything in At the Table",
      "Monthly members-only voice notes",
      "Full uncut episode chapters and transcripts",
      "Members-only Q&A once a season",
    ],
  },
  {
    name: "Sustainer",
    price: "$25/ month",
    status: "Coming Soon",
    features: [
      "Everything in The Inner Circle",
      "Named acknowledgment in the season notes (optional)",
      "A yearly hand-signed note from Stephanie",
      "First access to guest-request forms and future events",
    ],
  },
];

const PROMISES = [
  {
    number: "01",
    title: "No paywall on the podcast",
    content:
      "Every episode stays free on every platform, forever. Membership funds the work — it never gates it.",
  },
  {
    number: "02",
    title: "A small circle, on purpose",
    content:
      "This is not a scale play. The Inner Circle is capped so members-only spaces stay small enough for actual conversation.",
  },
  {
    number: "03",
    title: "Cancel any time",
    content:
      "One click. Your seat here shouldn’t feel like a subscription — it should feel like a decision you get to keep making.",
  },
];

const Membership = () => (
  <main className="bg-white">
    <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-5 lg:px-[60px]">
          <Words text="Membership" mode="load" delay={0.1} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]" />
          <h1 className="font-display text-[36px] leading-[1.08] text-black sm:text-[48px] lg:text-[58px] lg:leading-[64px]" aria-label="Three Ways to Sit Closer">
            <span className="block">
              <Words text="Three Ways" mode="load" delay={0.2} stagger={0.07} />
            </span>
            <span className="block">
              <Words text="to Sit Closer" mode="load" delay={0.36} stagger={0.07} />
            </span>
          </h1>
          <FadeUp mode="load" delay={0.55}>
            <p className="max-w-[620px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">
              The podcast stays free. Start with a seat at the community table — paid tiers unlock soon and will underwrite the work while keeping every episode
              free.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    <section className="w-full bg-white py-14 sm:py-16 lg:py-[72px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto grid max-w-[1208px] grid-cols-1 gap-6 md:grid-cols-3 lg:gap-7 lg:px-[60px]">
          {TIERS.map((t, i) => (
            <FadeUp key={t.name} delay={0.07 * i} y={24} className="h-full">
              <div
                data-testid={`membership-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative flex h-full flex-col gap-6 rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1 sm:p-10 ${
                  t.highlight ? "border-ink bg-white shadow-[0_28px_70px_-40px_rgba(8,9,10,0.4)]" : "border-hairline bg-white-smoke hover:border-silver"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-[24px] leading-[1.2] text-black sm:text-[28px]">{t.name}</h2>
                  <PopIn delay={0.1 * i}>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-[10.5px] uppercase leading-[16px] tracking-[0.22em] font-display ${
                        t.highlight ? "bg-linen text-black" : "border border-hairline bg-white text-charcoal-mid"
                      }`}
                    >
                      {t.status}
                    </span>
                  </PopIn>
                </div>
                <span className="font-display text-[40px] leading-[1] text-black sm:text-[46px]">{t.price}</span>
                <ul className="flex flex-col gap-3 border-t border-hairline pt-6">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13.8px] leading-[23px] text-charcoal">
                      <span className="mt-[3px] font-semibold text-charcoal-dark">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-2">
                  {t.cta ? (
                    <PillButton href={t.cta.href} dataTestId={`membership-join-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {t.cta.label}
                    </PillButton>
                  ) : (
                    <span
                      aria-disabled="true"
                      className="inline-flex cursor-not-allowed select-none items-center rounded-full border border-silver px-8 py-[9px] text-[15px] uppercase tracking-[0.08em] text-charcoal-mid font-display"
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp y={12}>
          <p className="mx-auto mt-8 max-w-[1208px] text-center text-[13.5px] leading-[23px] text-charcoal-mid lg:px-[60px]">
            Paid tiers are launching soon — only the Free tier is active today.
          </p>
        </FadeUp>
      </div>
    </section>

    <section className="w-full bg-white-smoke py-16 sm:py-20 lg:py-[88px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-10 lg:px-[60px]">
          <h2 className="font-display text-[30px] leading-[1.15] text-black sm:text-[36px] lg:text-[42px]">
            <Words text="Three promises" />
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {PROMISES.map((p, i) => (
              <FadeUp key={p.number} delay={0.07 * i} y={20} className="h-full">
                <div data-testid={`membership-promise-${p.number}`} className="flex h-full flex-col gap-3 rounded-2xl border border-hairline bg-white p-8">
                  <span className="font-display text-[13px] uppercase tracking-[0.22em] text-charcoal-mid">{p.number}</span>
                  <h3 className="font-display text-[21px] leading-[1.25] text-black sm:text-[24px]">{p.title}</h3>
                  <p className="text-[14px] leading-[24px] text-charcoal">{p.content}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="w-full bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col items-start gap-8 rounded-3xl bg-linen p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:px-[60px]">
          <div className="flex max-w-[560px] flex-col gap-3">
            <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[34px]">
              <Words text="Start free. The rest comes soon." />
            </h2>
            <FadeUp y={14}>
              <p className="text-[14.6px] leading-[26px] text-charcoal-dark sm:text-[15.2px]">
                The seat at the table is open today. When the paid tiers unlock, you’ll be the first to know.
              </p>
            </FadeUp>
          </div>
          <FadeUp y={12} className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <PillButton href="/episodes" dataTestId="membership-listen-first-button">
              Listen First
            </PillButton>
            <a href="/contact" data-testid="membership-ask-link" className="group inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal-dark sm:text-[16px]">
              <span className="transition-transform duration-500 ease-out group-hover:-translate-x-1">Ask a question first</span>
              <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">→</span>
            </a>
          </FadeUp>
        </div>
      </div>
    </section>
  </main>
);

export default Membership;
